/*
 * Copyright (C) 2026 Fluxer Contributors
 *
 * This file is part of Fluxer.
 *
 * Fluxer is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * Fluxer is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with Fluxer. If not, see <https://www.gnu.org/licenses/>.
 */

import type {ValueOf} from '@fluxer/constants/src/ValueOf';

export const APIErrorCodes = {
	ACCESS_DENIED: 'ACCESS_DENIED',
	ACCOUNT_DISABLED: 'ACCOUNT_DISABLED',
	BAD_GATEWAY: 'BAD_GATEWAY',
	BAD_REQUEST: 'BAD_REQUEST',
	BLUESKY_OAUTH_AUTHORIZATION_FAILED: 'BLUESKY_OAUTH_AUTHORIZATION_FAILED',
	BLUESKY_OAUTH_CALLBACK_FAILED: 'BLUESKY_OAUTH_CALLBACK_FAILED',
	BLUESKY_OAUTH_NOT_ENABLED: 'BLUESKY_OAUTH_NOT_ENABLED',
	BLUESKY_OAUTH_SESSION_EXPIRED: 'BLUESKY_OAUTH_SESSION_EXPIRED',
	BLUESKY_OAUTH_STATE_INVALID: 'BLUESKY_OAUTH_STATE_INVALID',
	ACCOUNT_SCHEDULED_FOR_DELETION: 'ACCOUNT_SCHEDULED_FOR_DELETION',
	ACCOUNT_SUSPENDED_PERMANENTLY: 'ACCOUNT_SUSPENDED_PERMANENTLY',
	ACCOUNT_SUSPENDED_TEMPORARILY: 'ACCOUNT_SUSPENDED_TEMPORARILY',
	ACCOUNT_SUSPICIOUS_ACTIVITY: 'ACCOUNT_SUSPICIOUS_ACTIVITY',
	ACCOUNT_TOO_NEW_FOR_GUILD: 'ACCOUNT_TOO_NEW_FOR_GUILD',
	ACLS_MUST_BE_NON_EMPTY: 'ACLS_MUST_BE_NON_EMPTY',
	ADMIN_API_KEY_NOT_FOUND: 'ADMIN_API_KEY_NOT_FOUND',
	APPLICATION_NOT_FOUND: 'APPLICATION_NOT_FOUND',
	APPLICATION_NOT_OWNED: 'APPLICATION_NOT_OWNED',
	ANTISPAM_RULE_NOT_FOUND: 'ANTISPAM_RULE_NOT_FOUND',
	ALREADY_FRIENDS: 'ALREADY_FRIENDS',
	AUDIT_LOG_INDEXING: 'AUDIT_LOG_INDEXING',
	BOTS_CANNOT_CREATE_GUILDS: 'BOTS_CANNOT_CREATE_GUILDS',
	BOTS_CANNOT_SEND_FRIEND_REQUESTS: 'BOTS_CANNOT_SEND_FRIEND_REQUESTS',
	BOT_ALREADY_IN_GUILD: 'BOT_ALREADY_IN_GUILD',
	BOT_APPLICATION_NOT_FOUND: 'BOT_APPLICATION_NOT_FOUND',
	BOT_IS_PRIVATE: 'BOT_IS_PRIVATE',
	BOT_USER_AUTH_ENDPOINT_ACCESS_DENIED: 'BOT_USER_AUTH_ENDPOINT_ACCESS_DENIED',
	BOT_USER_AUTH_SESSION_CREATION_DENIED: 'BOT_USER_AUTH_SESSION_CREATION_DENIED',
	BOT_USER_GENERATION_FAILED: 'BOT_USER_GENERATION_FAILED',
	BOT_USER_NOT_FOUND: 'BOT_USER_NOT_FOUND',
	CALL_ALREADY_EXISTS: 'CALL_ALREADY_EXISTS',
	CANNOT_EDIT_OTHER_USER_MESSAGE: 'CANNOT_EDIT_OTHER_USER_MESSAGE',
	CANNOT_EXECUTE_ON_DM: 'CANNOT_EXECUTE_ON_DM',
	CANNOT_BLOCK_SYSTEM_USER: 'CANNOT_BLOCK_SYSTEM_USER',
	CANNOT_MODIFY_SYSTEM_WEBHOOK: 'CANNOT_MODIFY_SYSTEM_WEBHOOK',
	CANNOT_MODIFY_VOICE_STATE: 'CANNOT_MODIFY_VOICE_STATE',
	CANNOT_REDEEM_PLUTONIUM_WITH_VISIONARY: 'CANNOT_REDEEM_PLUTONIUM_WITH_VISIONARY',
	CANNOT_REPORT_OWN_GUILD: 'CANNOT_REPORT_OWN_GUILD',
	CANNOT_REPORT_OWN_MESSAGE: 'CANNOT_REPORT_OWN_MESSAGE',
	CANNOT_REPORT_YOURSELF: 'CANNOT_REPORT_YOURSELF',
	CANNOT_SEND_EMPTY_MESSAGE: 'CANNOT_SEND_EMPTY_MESSAGE',
	CANNOT_SEND_FRIEND_REQUEST_TO_BLOCKED_USER: 'CANNOT_SEND_FRIEND_REQUEST_TO_BLOCKED_USER',
	CANNOT_SEND_FRIEND_REQUEST_TO_SELF: 'CANNOT_SEND_FRIEND_REQUEST_TO_SELF',
	CANNOT_SEND_MESSAGES_IN_NON_TEXT_CHANNEL: 'CANNOT_SEND_MESSAGES_IN_NON_TEXT_CHANNEL',
	CANNOT_SEND_MESSAGES_TO_USER: 'CANNOT_SEND_MESSAGES_TO_USER',
	CANNOT_TRANSFER_OWNERSHIP_TO_BOT: 'CANNOT_TRANSFER_OWNERSHIP_TO_BOT',
	CANNOT_SHRINK_RESERVED_SLOTS: 'CANNOT_SHRINK_RESERVED_SLOTS',
	CAPTCHA_REQUIRED: 'CAPTCHA_REQUIRED',
	CHANNEL_INDEXING: 'CHANNEL_INDEXING',
	COMMUNICATION_DISABLED: 'COMMUNICATION_DISABLED',
	CONNECTION_ALREADY_EXISTS: 'CONNECTION_ALREADY_EXISTS',
	CONNECTION_INITIATION_TOKEN_INVALID: 'CONNECTION_INITIATION_TOKEN_INVALID',
	CONNECTION_INVALID_IDENTIFIER: 'CONNECTION_INVALID_IDENTIFIER',
	CONNECTION_INVALID_TYPE: 'CONNECTION_INVALID_TYPE',
	CONNECTION_LIMIT_REACHED: 'CONNECTION_LIMIT_REACHED',
	CONNECTION_NOT_FOUND: 'CONNECTION_NOT_FOUND',
	CONNECTION_VERIFICATION_FAILED: 'CONNECTION_VERIFICATION_FAILED',
	CONFLICT: 'CONFLICT',
	CONTENT_BLOCKED: 'CONTENT_BLOCKED',
	CREATION_FAILED: 'CREATION_FAILED',
	CSAM_SCAN_FAILED: 'CSAM_SCAN_FAILED',
	CSAM_SCAN_PARSE_ERROR: 'CSAM_SCAN_PARSE_ERROR',
	CSAM_SCAN_SUBSCRIPTION_ERROR: 'CSAM_SCAN_SUBSCRIPTION_ERROR',
	CSAM_SCAN_TIMEOUT: 'CSAM_SCAN_TIMEOUT',
	DECRYPTION_FAILED: 'DECRYPTION_FAILED',
	DELETION_FAILED: 'DELETION_FAILED',
	DISCOVERY_ALREADY_APPLIED: 'DISCOVERY_ALREADY_APPLIED',
	DISCOVERY_APPLICATION_ALREADY_REVIEWED: 'DISCOVERY_APPLICATION_ALREADY_REVIEWED',
	DISCOVERY_APPLICATION_NOT_FOUND: 'DISCOVERY_APPLICATION_NOT_FOUND',
	DISCOVERY_DESCRIPTION_REQUIRED: 'DISCOVERY_DESCRIPTION_REQUIRED',
	DISCOVERY_DISABLED: 'DISCOVERY_DISABLED',
	DISCOVERY_INSUFFICIENT_MEMBERS: 'DISCOVERY_INSUFFICIENT_MEMBERS',
	DISCOVERY_INVALID_CATEGORY: 'DISCOVERY_INVALID_CATEGORY',
	DISCOVERY_NOT_DISCOVERABLE: 'DISCOVERY_NOT_DISCOVERABLE',
	DISCRIMINATOR_REQUIRED: 'DISCRIMINATOR_REQUIRED',
	EMAIL_SERVICE_NOT_TESTABLE: 'EMAIL_SERVICE_NOT_TESTABLE',
	EMAIL_VERIFICATION_REQUIRED: 'EMAIL_VERIFICATION_REQUIRED',
	EMPTY_ENCRYPTED_BODY: 'EMPTY_ENCRYPTED_BODY',
	ENCRYPTION_FAILED: 'ENCRYPTION_FAILED',
	EXPLICIT_CONTENT_CANNOT_BE_SENT: 'EXPLICIT_CONTENT_CANNOT_BE_SENT',
	FEATURE_NOT_AVAILABLE_SELF_HOSTED: 'FEATURE_NOT_AVAILABLE_SELF_HOSTED',
	FEATURE_TEMPORARILY_DISABLED: 'FEATURE_TEMPORARILY_DISABLED',
	FILE_SIZE_TOO_LARGE: 'FILE_SIZE_TOO_LARGE',
	FORBIDDEN: 'FORBIDDEN',
	FRIEND_REQUEST_BLOCKED: 'FRIEND_REQUEST_BLOCKED',
	GATEWAY_TIMEOUT: 'GATEWAY_TIMEOUT',
	GENERAL_ERROR: 'GENERAL_ERROR',
	GONE: 'GONE',
	GIFT_CODE_ALREADY_REDEEMED: 'GIFT_CODE_ALREADY_REDEEMED',
	GUILD_PHONE_VERIFICATION_REQUIRED: 'GUILD_PHONE_VERIFICATION_REQUIRED',
	GUILD_TEMPLATE_FETCH_FAILED: 'GUILD_TEMPLATE_FETCH_FAILED',
	GUILD_TEMPLATE_INVALID: 'GUILD_TEMPLATE_INVALID',
	GUILD_VERIFICATION_REQUIRED: 'GUILD_VERIFICATION_REQUIRED',
	HANDOFF_CODE_EXPIRED: 'HANDOFF_CODE_EXPIRED',
	HARVEST_EXPIRED: 'HARVEST_EXPIRED',
	HARVEST_FAILED: 'HARVEST_FAILED',
	HARVEST_NOT_READY: 'HARVEST_NOT_READY',
	HARVEST_ON_COOLDOWN: 'HARVEST_ON_COOLDOWN',
	HTTP_GET_AUTHORIZE_NOT_SUPPORTED: 'HTTP_GET_AUTHORIZE_NOT_SUPPORTED',
	INSTANCE_VERSION_MISMATCH: 'INSTANCE_VERSION_MISMATCH',
	INTERNAL_SERVER_ERROR: 'INTERNAL_SERVER_ERROR',
	INVALID_ACLS_FORMAT: 'INVALID_ACLS_FORMAT',
	INVALID_API_ORIGIN: 'INVALID_API_ORIGIN',
	INVALID_AUTH_TOKEN: 'INVALID_AUTH_TOKEN',
	INVALID_BOT_FLAG: 'INVALID_BOT_FLAG',
	INVALID_CAPTCHA: 'INVALID_CAPTCHA',
	INVALID_CHANNEL_TYPE_FOR_CALL: 'INVALID_CHANNEL_TYPE_FOR_CALL',
	INVALID_CHANNEL_TYPE: 'INVALID_CHANNEL_TYPE',
	INVALID_CLIENT: 'INVALID_CLIENT',
	INVALID_CLIENT_SECRET: 'INVALID_CLIENT_SECRET',
	INVALID_DSA_REPORT_TARGET: 'INVALID_DSA_REPORT_TARGET',
	INVALID_DSA_TICKET: 'INVALID_DSA_TICKET',
	INVALID_DSA_VERIFICATION_CODE: 'INVALID_DSA_VERIFICATION_CODE',
	INVALID_DECRYPTED_JSON: 'INVALID_DECRYPTED_JSON',
	INVALID_EPHEMERAL_KEY: 'INVALID_EPHEMERAL_KEY',
	INVALID_FLAGS_FORMAT: 'INVALID_FLAGS_FORMAT',
	INVALID_IV: 'INVALID_IV',
	INVALID_FORM_BODY: 'INVALID_FORM_BODY',
	INVALID_GRANT: 'INVALID_GRANT',
	INVALID_HANDOFF_CODE: 'INVALID_HANDOFF_CODE',
	INVALID_PACK_TYPE: 'INVALID_PACK_TYPE',
	INVALID_PERMISSIONS_INTEGER: 'INVALID_PERMISSIONS_INTEGER',
	INVALID_PERMISSIONS_NEGATIVE: 'INVALID_PERMISSIONS_NEGATIVE',
	INVALID_PHONE_NUMBER: 'INVALID_PHONE_NUMBER',
	INVALID_PHONE_VERIFICATION_CODE: 'INVALID_PHONE_VERIFICATION_CODE',
	INVALID_REDIRECT_URI: 'INVALID_REDIRECT_URI',
	INVALID_REQUEST: 'INVALID_REQUEST',
	INVALID_RESPONSE_TYPE_FOR_NON_BOT: 'INVALID_RESPONSE_TYPE_FOR_NON_BOT',
	INVALID_SCOPE: 'INVALID_SCOPE',
	INVALID_STREAM_KEY_FORMAT: 'INVALID_STREAM_KEY_FORMAT',
	INVALID_STREAM_THUMBNAIL_PAYLOAD: 'INVALID_STREAM_THUMBNAIL_PAYLOAD',
	INVALID_SUDO_TOKEN: 'INVALID_SUDO_TOKEN',
	INVALID_SUSPICIOUS_FLAGS_FORMAT: 'INVALID_SUSPICIOUS_FLAGS_FORMAT',
	INVALID_SYSTEM_FLAG: 'INVALID_SYSTEM_FLAG',
	INVALID_TIMESTAMP: 'INVALID_TIMESTAMP',
	INVALID_TOKEN: 'INVALID_TOKEN',
	INVALID_WEBAUTHN_AUTHENTICATION_COUNTER: 'INVALID_WEBAUTHN_AUTHENTICATION_COUNTER',
	INVALID_WEBAUTHN_CREDENTIAL_COUNTER: 'INVALID_WEBAUTHN_CREDENTIAL_COUNTER',
	INVALID_WEBAUTHN_CREDENTIAL: 'INVALID_WEBAUTHN_CREDENTIAL',
	INVALID_WEBAUTHN_PUBLIC_KEY_FORMAT: 'INVALID_WEBAUTHN_PUBLIC_KEY_FORMAT',
	INVITES_DISABLED: 'INVITES_DISABLED',
	IP_AUTHORIZATION_REQUIRED: 'IP_AUTHORIZATION_REQUIRED',
	IP_AUTHORIZATION_RESEND_COOLDOWN: 'IP_AUTHORIZATION_RESEND_COOLDOWN',
	IP_AUTHORIZATION_RESEND_LIMIT_EXCEEDED: 'IP_AUTHORIZATION_RESEND_LIMIT_EXCEEDED',
	IP_BANNED: 'IP_BANNED',
	RESIDENTIAL_PROXY_BLOCKED: 'RESIDENTIAL_PROXY_BLOCKED',
	TOR_BLOCKED: 'TOR_BLOCKED',
	MAX_ANIMATED_EMOJIS: 'MAX_ANIMATED_EMOJIS',
	MAX_APPLICATIONS: 'MAX_APPLICATIONS',
	MAX_BOOKMARKS: 'MAX_BOOKMARKS',
	MAX_CATEGORY_CHANNELS: 'MAX_CATEGORY_CHANNELS',
	MAX_EMOJIS: 'MAX_EMOJIS',
	MAX_FAVORITE_MEMES: 'MAX_FAVORITE_MEMES',
	MAX_FRIENDS: 'MAX_FRIENDS',
	MAX_GROUP_DM_RECIPIENTS: 'MAX_GROUP_DM_RECIPIENTS',
	MAX_GROUP_DMS: 'MAX_GROUP_DMS',
	MAX_GUILD_CHANNELS: 'MAX_GUILD_CHANNELS',
	MAX_GUILD_MEMBERS: 'MAX_GUILD_MEMBERS',
	MAX_GUILD_ROLES: 'MAX_GUILD_ROLES',
	MAX_GUILDS: 'MAX_GUILDS',
	NEW_ACCOUNT_GUILD_JOIN_RATE_LIMITED: 'NEW_ACCOUNT_GUILD_JOIN_RATE_LIMITED',
	MAX_INVITES: 'MAX_INVITES',
	MAX_PACK_EXPRESSIONS: 'MAX_PACK_EXPRESSIONS',
	MAX_PACKS: 'MAX_PACKS',
	MAX_PINS_PER_CHANNEL: 'MAX_PINS_PER_CHANNEL',
	MESSAGE_TOTAL_ATTACHMENT_SIZE_TOO_LARGE: 'MESSAGE_TOTAL_ATTACHMENT_SIZE_TOO_LARGE',
	MAX_REACTIONS: 'MAX_REACTIONS',
	MAX_STICKERS: 'MAX_STICKERS',
	MAX_WEBHOOKS_PER_CHANNEL: 'MAX_WEBHOOKS_PER_CHANNEL',
	MAX_WEBHOOKS_PER_GUILD: 'MAX_WEBHOOKS_PER_GUILD',
	MAX_WEBHOOKS: 'MAX_WEBHOOKS',
	NCMEC_ALREADY_SUBMITTED: 'NCMEC_ALREADY_SUBMITTED',
	NCMEC_SUBMISSION_FAILED: 'NCMEC_SUBMISSION_FAILED',
	MEDIA_METADATA_ERROR: 'MEDIA_METADATA_ERROR',
	METHOD_NOT_ALLOWED: 'METHOD_NOT_ALLOWED',
	MISSING_ACCESS: 'MISSING_ACCESS',
	MISSING_ACL: 'MISSING_ACL',
	MISSING_AUTHORIZATION: 'MISSING_AUTHORIZATION',
	MISSING_CLIENT_SECRET: 'MISSING_CLIENT_SECRET',
	MISSING_EPHEMERAL_KEY: 'MISSING_EPHEMERAL_KEY',
	MISSING_IV: 'MISSING_IV',
	MISSING_OAUTH_ADMIN_SCOPE: 'MISSING_OAUTH_ADMIN_SCOPE',
	MISSING_OAUTH_FIELDS: 'MISSING_OAUTH_FIELDS',
	MISSING_OAUTH_SCOPE: 'MISSING_OAUTH_SCOPE',
	MISSING_PERMISSIONS: 'MISSING_PERMISSIONS',
	MISSING_REDIRECT_URI: 'MISSING_REDIRECT_URI',
	NO_ACTIVE_CALL: 'NO_ACTIVE_CALL',
	NO_ACTIVE_SUBSCRIPTION: 'NO_ACTIVE_SUBSCRIPTION',
	NOT_FOUND: 'NOT_FOUND',
	NOT_IMPLEMENTED: 'NOT_IMPLEMENTED',
	NO_PASSKEYS_REGISTERED: 'NO_PASSKEYS_REGISTERED',
	NO_PENDING_DELETION: 'NO_PENDING_DELETION',
	NO_USERS_WITH_FLUXERTAG_EXIST: 'NO_USERS_WITH_FLUXERTAG_EXIST',
	NO_VISIONARY_SLOTS_AVAILABLE: 'NO_VISIONARY_SLOTS_AVAILABLE',
	NOT_A_BOT_APPLICATION: 'NOT_A_BOT_APPLICATION',
	NOT_FRIENDS_WITH_USER: 'NOT_FRIENDS_WITH_USER',
	NOT_OWNER_OF_ADMIN_API_KEY: 'NOT_OWNER_OF_ADMIN_API_KEY',
	NSFW_CONTENT_AGE_RESTRICTED: 'NSFW_CONTENT_AGE_RESTRICTED',
	NSFW_EMOJI_STICKER_BLOCKED: 'NSFW_EMOJI_STICKER_BLOCKED',
	PACK_ACCESS_DENIED: 'PACK_ACCESS_DENIED',
	PASSKEY_AUTHENTICATION_FAILED: 'PASSKEY_AUTHENTICATION_FAILED',
	PASSKEYS_DISABLED: 'PASSKEYS_DISABLED',
	PHONE_ADD_NOT_ELIGIBLE: 'PHONE_ADD_NOT_ELIGIBLE',
	PHONE_ALREADY_USED: 'PHONE_ALREADY_USED',
	PHONE_RATE_LIMIT_EXCEEDED: 'PHONE_RATE_LIMIT_EXCEEDED',
	PHONE_REQUIRED_FOR_SMS_MFA: 'PHONE_REQUIRED_FOR_SMS_MFA',
	PHONE_VERIFICATION_REQUIRED: 'PHONE_VERIFICATION_REQUIRED',
	PREMIUM_PURCHASE_BLOCKED: 'PREMIUM_PURCHASE_BLOCKED',
	PREVIEW_MUST_BE_JPEG: 'PREVIEW_MUST_BE_JPEG',
	PROCESSING_FAILED: 'PROCESSING_FAILED',
	RATE_LIMITED: 'RATE_LIMITED',
	REDIRECT_URI_REQUIRED_FOR_NON_BOT: 'REDIRECT_URI_REQUIRED_FOR_NON_BOT',
	REPORT_ALREADY_RESOLVED: 'REPORT_ALREADY_RESOLVED',
	REPORT_BANNED: 'REPORT_BANNED',
	RESPONSE_VALIDATION_ERROR: 'RESPONSE_VALIDATION_ERROR',
	SERVICE_UNAVAILABLE: 'SERVICE_UNAVAILABLE',
	SESSION_TOKEN_MISMATCH: 'SESSION_TOKEN_MISMATCH',
	SLOWMODE_RATE_LIMITED: 'SLOWMODE_RATE_LIMITED',
	SMS_MFA_NOT_ENABLED: 'SMS_MFA_NOT_ENABLED',
	SMS_MFA_REQUIRES_TOTP: 'SMS_MFA_REQUIRES_TOTP',
	SMS_VERIFICATION_UNAVAILABLE: 'SMS_VERIFICATION_UNAVAILABLE',
	SQL_QUERY_REJECTED: 'SQL_QUERY_REJECTED',
	UNKNOWN_ANTI_SPAM_RULE: 'UNKNOWN_ANTI_SPAM_RULE',
	INVALID_ANTI_SPAM_TRIGGER_TYPE: 'INVALID_ANTI_SPAM_TRIGGER_TYPE',
	INVALID_ANTI_SPAM_ACTION: 'INVALID_ANTI_SPAM_ACTION',
	INVALID_ANTI_SPAM_RULE_CODE: 'INVALID_ANTI_SPAM_RULE_CODE',
	SSO_REQUIRED: 'SSO_REQUIRED',
	STREAM_KEY_CHANNEL_MISMATCH: 'STREAM_KEY_CHANNEL_MISMATCH',
	STREAM_KEY_SCOPE_MISMATCH: 'STREAM_KEY_SCOPE_MISMATCH',
	STREAM_THUMBNAIL_PAYLOAD_EMPTY: 'STREAM_THUMBNAIL_PAYLOAD_EMPTY',
	STRIPE_ERROR: 'STRIPE_ERROR',
	STRIPE_GIFT_REDEMPTION_IN_PROGRESS: 'STRIPE_GIFT_REDEMPTION_IN_PROGRESS',
	STRIPE_INVALID_PRODUCT: 'STRIPE_INVALID_PRODUCT',
	STRIPE_INVALID_PRODUCT_CONFIGURATION: 'STRIPE_INVALID_PRODUCT_CONFIGURATION',
	STRIPE_NO_ACTIVE_SUBSCRIPTION: 'STRIPE_NO_ACTIVE_SUBSCRIPTION',
	STRIPE_NO_PURCHASE_HISTORY: 'STRIPE_NO_PURCHASE_HISTORY',
	STRIPE_NO_SUBSCRIPTION: 'STRIPE_NO_SUBSCRIPTION',
	STRIPE_PAYMENT_NOT_AVAILABLE: 'STRIPE_PAYMENT_NOT_AVAILABLE',
	STRIPE_SUBSCRIPTION_ALREADY_CANCELING: 'STRIPE_SUBSCRIPTION_ALREADY_CANCELING',
	STRIPE_SUBSCRIPTION_NOT_CANCELING: 'STRIPE_SUBSCRIPTION_NOT_CANCELING',
	STRIPE_SUBSCRIPTION_PERIOD_END_MISSING: 'STRIPE_SUBSCRIPTION_PERIOD_END_MISSING',
	STRIPE_WEBHOOK_NOT_AVAILABLE: 'STRIPE_WEBHOOK_NOT_AVAILABLE',
	STRIPE_WEBHOOK_SIGNATURE_INVALID: 'STRIPE_WEBHOOK_SIGNATURE_INVALID',
	STRIPE_WEBHOOK_SIGNATURE_MISSING: 'STRIPE_WEBHOOK_SIGNATURE_MISSING',
	DONATION_AMOUNT_INVALID: 'DONATION_AMOUNT_INVALID',
	DONATION_MAGIC_LINK_EXPIRED: 'DONATION_MAGIC_LINK_EXPIRED',
	DONATION_MAGIC_LINK_INVALID: 'DONATION_MAGIC_LINK_INVALID',
	DONATION_MAGIC_LINK_USED: 'DONATION_MAGIC_LINK_USED',
	DONOR_NOT_FOUND: 'DONOR_NOT_FOUND',
	SUDO_MODE_REQUIRED: 'SUDO_MODE_REQUIRED',
	TAG_ALREADY_TAKEN: 'TAG_ALREADY_TAKEN',
	TEMPORARY_INVITE_REQUIRES_PRESENCE: 'TEMPORARY_INVITE_REQUIRES_PRESENCE',
	TEST_HARNESS_DISABLED: 'TEST_HARNESS_DISABLED',
	TEST_HARNESS_FORBIDDEN: 'TEST_HARNESS_FORBIDDEN',
	TWO_FA_NOT_ENABLED: 'TWO_FA_NOT_ENABLED',
	TWO_FACTOR_REQUIRED: 'TWO_FACTOR_REQUIRED',
	UNAUTHORIZED: 'UNAUTHORIZED',
	UNCLAIMED_ACCOUNT_CANNOT_ACCEPT_FRIEND_REQUESTS: 'UNCLAIMED_ACCOUNT_CANNOT_ACCEPT_FRIEND_REQUESTS',
	UNCLAIMED_ACCOUNT_CANNOT_ADD_REACTIONS: 'UNCLAIMED_ACCOUNT_CANNOT_ADD_REACTIONS',
	UNCLAIMED_ACCOUNT_CANNOT_CREATE_APPLICATIONS: 'UNCLAIMED_ACCOUNT_CANNOT_CREATE_APPLICATIONS',
	UNCLAIMED_ACCOUNT_CANNOT_CREATE_GUILDS: 'UNCLAIMED_ACCOUNT_CANNOT_CREATE_GUILDS',
	UNCLAIMED_ACCOUNT_CANNOT_JOIN_GROUP_DMS: 'UNCLAIMED_ACCOUNT_CANNOT_JOIN_GROUP_DMS',
	UNCLAIMED_ACCOUNT_CANNOT_JOIN_ONE_ON_ONE_VOICE_CALLS: 'UNCLAIMED_ACCOUNT_CANNOT_JOIN_ONE_ON_ONE_VOICE_CALLS',
	UNCLAIMED_ACCOUNT_CANNOT_JOIN_VOICE_CHANNELS: 'UNCLAIMED_ACCOUNT_CANNOT_JOIN_VOICE_CHANNELS',
	UNCLAIMED_ACCOUNT_CANNOT_MAKE_PURCHASES: 'UNCLAIMED_ACCOUNT_CANNOT_MAKE_PURCHASES',
	UNCLAIMED_ACCOUNT_CANNOT_SEND_DIRECT_MESSAGES: 'UNCLAIMED_ACCOUNT_CANNOT_SEND_DIRECT_MESSAGES',
	UNCLAIMED_ACCOUNT_CANNOT_SEND_FRIEND_REQUESTS: 'UNCLAIMED_ACCOUNT_CANNOT_SEND_FRIEND_REQUESTS',
	UNCLAIMED_ACCOUNT_CANNOT_SEND_MESSAGES: 'UNCLAIMED_ACCOUNT_CANNOT_SEND_MESSAGES',
	UNCLAIMED_ACCOUNT_CANNOT_SUBMIT_REPORTS: 'UNCLAIMED_ACCOUNT_CANNOT_SUBMIT_REPORTS',
	UNKNOWN_CHANNEL: 'UNKNOWN_CHANNEL',
	UNKNOWN_EMOJI: 'UNKNOWN_EMOJI',
	UNKNOWN_FAVORITE_MEME: 'UNKNOWN_FAVORITE_MEME',
	UNKNOWN_GIFT_CODE: 'UNKNOWN_GIFT_CODE',
	UNKNOWN_GUILD: 'UNKNOWN_GUILD',
	UNKNOWN_HARVEST: 'UNKNOWN_HARVEST',
	UNKNOWN_INVITE: 'UNKNOWN_INVITE',
	UNKNOWN_MEMBER: 'UNKNOWN_MEMBER',
	UNKNOWN_MESSAGE: 'UNKNOWN_MESSAGE',
	UNKNOWN_PACK: 'UNKNOWN_PACK',
	UNKNOWN_REPORT: 'UNKNOWN_REPORT',
	UNKNOWN_ROLE: 'UNKNOWN_ROLE',
	UNKNOWN_STICKER: 'UNKNOWN_STICKER',
	UNKNOWN_SUSPICIOUS_FLAG: 'UNKNOWN_SUSPICIOUS_FLAG',
	UNKNOWN_USER_FLAG: 'UNKNOWN_USER_FLAG',
	UNKNOWN_USER: 'UNKNOWN_USER',
	UNKNOWN_VOICE_REGION: 'UNKNOWN_VOICE_REGION',
	UNKNOWN_VOICE_SERVER: 'UNKNOWN_VOICE_SERVER',
	UNKNOWN_WEBAUTHN_CREDENTIAL: 'UNKNOWN_WEBAUTHN_CREDENTIAL',
	UNKNOWN_APPLICATION: 'UNKNOWN_APPLICATION',
	UNKNOWN_WEBHOOK: 'UNKNOWN_WEBHOOK',
	UNSUPPORTED_RESPONSE_TYPE: 'UNSUPPORTED_RESPONSE_TYPE',
	USERNAME_NOT_AVAILABLE: 'USERNAME_NOT_AVAILABLE',
	UPDATE_FAILED: 'UPDATE_FAILED',
	USER_BANNED_FROM_GUILD: 'USER_BANNED_FROM_GUILD',
	USER_IP_BANNED_FROM_GUILD: 'USER_IP_BANNED_FROM_GUILD',
	USER_NOT_IN_VOICE: 'USER_NOT_IN_VOICE',
	USER_OWNS_GUILDS: 'USER_OWNS_GUILDS',
	VALIDATION_ERROR: 'VALIDATION_ERROR',
	VOICE_CHANNEL_FULL: 'VOICE_CHANNEL_FULL',
	WEBAUTHN_CREDENTIAL_LIMIT_REACHED: 'WEBAUTHN_CREDENTIAL_LIMIT_REACHED',
	AGE_VERIFICATION_ALREADY_VERIFIED: 'AGE_VERIFICATION_ALREADY_VERIFIED',
	AGE_VERIFICATION_INVALID_CARD_TYPE: 'AGE_VERIFICATION_INVALID_CARD_TYPE',
} as const;

export type APIErrorCode = ValueOf<typeof APIErrorCodes>;
